"use client";

import { useState, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Users, FileText, TrendingUp, CreditCard } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
} from "recharts";
import { adminApi } from "@/lib/adminApi";

interface Stats {
  totalUsers: number;
  totalProposals: number;
  acceptanceRate: number;
  totalRevenue: number;
  monthlyRevenue: number;
}

interface ChartData {
  month: string;
  proposals?: number;
  users?: number;
  acceptanceRate?: number;
  revenue?: number;
}

interface RecentProposal {
  id: string;
  title: string;
  proposeeName: string;
  status: string;
  createdAt: string;
}

const statusColors: Record<string, string> = {
  DRAFT: "bg-zinc-100",
  PUBLISHED: "bg-emerald-100",
  SENT: "bg-blue-100 dark:bg-blue-900/50",
  ACCEPTED: "bg-purple-100",
  DECLINED: "bg-red-100 dark:bg-red-900/50",
};

export default function AnalyticsPage() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [proposalTrend, setProposalTrend] = useState<ChartData[]>([]);
  const [userGrowth, setUserGrowth] = useState<ChartData[]>([]);
  const [acceptanceData, setAcceptanceData] = useState<ChartData[]>([]);
  const [revenueData, setRevenueData] = useState<ChartData[]>([]);
  const [recentProposals, setRecentProposals] = useState<RecentProposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchAnalytics() {
      try {
        const data = await adminApi.post<{
          success: boolean;
          stats: Stats;
          proposalTrend: ChartData[];
          userGrowth: ChartData[];
          acceptanceData: ChartData[];
          revenueData: ChartData[];
        }>("/admin/stats/analytics", {});

        if (data.success) {
          setStats(data.stats);
          setProposalTrend(data.proposalTrend);
          setUserGrowth(data.userGrowth);
          setAcceptanceData(data.acceptanceData);
          setRevenueData(data.revenueData);
        }
      } catch (err) {
        console.error("Failed to fetch analytics:", err);
        setError("Failed to load analytics data");
      } finally {
        setLoading(false);
      }
    }
    fetchAnalytics();
  }, []);

  const tooltipStyle = {
    backgroundColor: "var(--tooltip-bg, #18181b)",
    border: "1px solid var(--tooltip-border, #27272a)",
    borderRadius: "0.5rem",
    color: "#fafafa",
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Analytics</h1>
          <p className="text-sm">
            Track platform performance and growth metrics.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="p-6 animate-pulse">
                <div className="h-8 w-8 rounded mb-4" />
                <div className="h-8 w-20 rounded" />
                <div className="h-4 w-16 rounded mt-2" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Analytics</h1>
          <p className="text-sm text-red-500">{error || "Failed to load analytics"}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Analytics</h1>
        <p className="text-sm">
          Track platform performance and growth metrics.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm">Total Users</p>
                <p className="text-2xl font-bold">
                  {stats.totalUsers.toLocaleString()}
                </p>
              </div>
              <Users className="h-8 w-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm">Total Proposals</p>
                <p className="text-2xl font-bold">
                  {stats.totalProposals.toLocaleString()}
                </p>
              </div>
              <FileText className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm">Acceptance Rate</p>
                <p className="text-2xl font-bold">
                  {stats.acceptanceRate}%
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm">Total Revenue</p>
                <p className="text-2xl font-bold">
                  ${(stats.totalRevenue / 100).toLocaleString()}
                </p>
              </div>
              <CreditCard className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Proposals Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={proposalTrend}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-zinc-200 dark:stroke-zinc-800" />
                  <XAxis dataKey="month" className="text-xs fill-zinc-500 dark:fill-zinc-400" />
                  <YAxis className="text-xs fill-zinc-500 dark:fill-zinc-400" />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Line
                    type="monotone"
                    dataKey="proposals"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={{ fill: "#10b981", strokeWidth: 0 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>User Growth</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={userGrowth}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-zinc-200 dark:stroke-zinc-800" />
                  <XAxis dataKey="month" className="text-xs fill-zinc-500 dark:fill-zinc-400" />
                  <YAxis className="text-xs fill-zinc-500 dark:fill-zinc-400" />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Area
                    type="monotone"
                    dataKey="users"
                    stroke="#10b981"
                    fill="#10b981"
                    fillOpacity={0.15}
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Acceptance vs Decline Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={acceptanceData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-zinc-200 dark:stroke-zinc-800" />
                  <XAxis dataKey="month" className="text-xs fill-zinc-500 dark:fill-zinc-400" />
                  <YAxis className="text-xs fill-zinc-500 dark:fill-zinc-400" />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="acceptanceRate" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-zinc-200 dark:stroke-zinc-800" />
                  <XAxis dataKey="month" className="text-xs fill-zinc-500 dark:fill-zinc-400" />
                  <YAxis className="text-xs fill-zinc-500 dark:fill-zinc-400" />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#f59e0b"
                    fill="#f59e0b"
                    fillOpacity={0.15}
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent Proposals</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {recentProposals.length === 0 ? (
            <p className="p-6 text-sm">No proposals yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide">
                      Proposee
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
                  {recentProposals.map((p) => (
                    <tr key={p.id} className="hover:bg-zinc-50/50">
                      <td className="px-6 py-4 text-sm font-mono">{p.id.slice(0, 8)}</td>
                      <td className="px-6 py-4 text-sm font-medium truncate max-w-[200px]">{p.title}</td>
                      <td className="px-6 py-4 text-sm">{p.proposeeName}</td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                            statusColors[p.status] || statusColors.DRAFT
                          }`}
                        >
                          {p.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {new Date(p.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}